import { useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { isEmpty } from 'lodash';
import { useLocation } from 'react-router-dom';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import FirstViewLogo from 'layouts/AuthLayout/components/FirstViewLogo';
import { useGetLowerUserPermissions } from 'services/hooks/users';
import { isFalseStrict } from 'utilities';
import LoadingPaper from './components/LoadingPaper';
import LogInForm from './components/LogInForm';
import OrganizationSelector from './components/OrganizationSelector';
import ProductSelector from './components/ProductSelector';
import { LoginProps } from './LogIn.props';
import { withRouter } from '../../services/hooks/with-router/withRouter';
import ResetPasswordForm from '../PasswordReset/ResetPasswordForm';

const StyledGridContainer = styled(Grid)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  minHeight: '100%',
}));

const StyledFormContainer = styled(Grid)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(8),
  flexDirection: 'column',
}));

const Login = (props: LoginProps) => {
  const [{ user }] = useStore() as unknown as UseStoreValues;
  const [permissions, fetchPermissions] = useGetLowerUserPermissions();
  const [passwordRecovery, setPasswordRecovery] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const location = useLocation();
  const hasDeepLink = Boolean(location.state?.from?.pathname && location.state?.from?.search);

  const isAuthenticating = useMemo(() => localStorage.getItem('access') && !user, [user]);

  // Apps Access
  const [hasAccessToBothApps, setHasAccessToBothApps] = useState<boolean | undefined>(undefined);
  const [hasAccessToInvestor, setHasAccessToInvestor] = useState<boolean | undefined>(undefined);
  const [hasAccessToDashboard, setHasAccessToDashboard] = useState<boolean | undefined>(undefined);

  // get other permissions
  useEffect(() => {
    if (user && !isEmpty(user)) {
      fetchPermissions(user.id as number);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Apps Access
  useEffect(() => {
    if (!isEmpty(user) && !isAuthenticating) {
      // firm permissions are frequently empty for superusers
      // actual standalone companies permissions instances will probably be none for superusers
      // but a serializer will return data as if they existed.
      const hasFirmsAccess = user?.is_superuser || (user?.firms_permissions?.length ?? 0) > 0;
      const hasDashboardAccess = (user?.standalone_companies_permissions?.length ?? 0) > 0 || user?.is_staff;

      setHasAccessToBothApps(hasFirmsAccess && hasDashboardAccess);
      setHasAccessToInvestor(hasFirmsAccess);
      setHasAccessToDashboard(hasDashboardAccess);
    } else {
      setHasAccessToBothApps(undefined);
      setHasAccessToInvestor(undefined);
      setHasAccessToDashboard(undefined);
    }
  }, [user, isAuthenticating]);

  return (
    <>
      <LoadingPaper isFullScreen loading={loading} message="Loading..." />
      <StyledGridContainer container style={loading ? { display: 'none' } : {}}>
        {/* Odd breakpoint issue requiring size overriding in the sx block */}
        <Grid size={{ lg: 6, xs: 12 }} sx={{ display: { lg: 'block', md: 'none' } }}>
          <FirstViewLogo loading={loading} setLoading={setLoading} />
        </Grid>
        <Grid size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
          <StyledFormContainer>
            {isEmpty(user) && !isAuthenticating && !passwordRecovery && (
              <LogInForm {...props} setPasswordRecovery={setPasswordRecovery} />
            )}

            {isEmpty(user) && !isAuthenticating && passwordRecovery && (
              <ResetPasswordForm setPasswordRecovery={setPasswordRecovery} />
            )}

            {isEmpty(user) && isAuthenticating && <LoadingPaper />}

            {hasAccessToBothApps && !hasDeepLink ? (
              // Product Selector - skip if there's a deep link
              <ProductSelector permissions={permissions} />
            ) : (
              <>
                {hasAccessToInvestor || hasAccessToDashboard ? (
                  //  Firm Selector | 409A Company Selector
                  <OrganizationSelector
                    lowerPermissions={permissions}
                    isDashboard={Boolean(hasAccessToDashboard && !hasAccessToInvestor)}
                  />
                ) : (
                  /* Default (No Access) Selector -- if feature flag enabled, OrganizationSelector loads Add Firm   */
                  isFalseStrict(hasAccessToBothApps) &&
                  isFalseStrict(hasAccessToInvestor) &&
                  isFalseStrict(hasAccessToDashboard) && <OrganizationSelector lowerPermissions={permissions} />
                )}
              </>
            )}
          </StyledFormContainer>
        </Grid>
      </StyledGridContainer>
    </>
  );
};

export default withRouter(Login);
